import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { API } from '@/App';
import { formatDate } from '@/utils/dateFormat';
import { ListTodo, Calendar, IndianRupee, Package, Clock, CheckCircle2, Circle, AlertCircle, Filter, Search, Edit2, Save, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import ProgressSlider, { getProgressBgClass } from '@/components/ProgressSlider';

export default function VendorTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [editData, setEditData] = useState({
    start_date: '',
    end_date: '',
    progress_percentage: 0,
    status: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchVendorTasks();
  }, []);

  const fetchVendorTasks = async () => {
    try {
      const response = await axios.get(`${API}/vendor/tasks`);
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setEditData({
      start_date: task.start_date?.split('T')[0] || '',
      end_date: task.end_date?.split('T')[0] || '',
      progress_percentage: task.progress_percentage || 0,
      status: task.status || 'not_started'
    });
    setEditDialogOpen(true);
  };

  const handleSaveTask = async () => {
    if (!selectedTask) return;
    
    setSaving(true);
    try {
      const params = new URLSearchParams();
      if (editData.start_date) params.append('start_date', editData.start_date);
      if (editData.end_date) params.append('end_date', editData.end_date);
      params.append('progress_percentage', editData.progress_percentage);
      params.append('status', editData.status);
      
      await axios.put(`${API}/vendor/tasks/${selectedTask.id}?${params.toString()}`);
      toast.success('Task updated successfully');
      setEditDialogOpen(false);
      fetchVendorTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    } finally {
      setSaving(false);
    }
  };

  // Get unique projects for filter
  const projects = [...new Set(tasks.map(t => t.project_name))];

  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    const matchesSearch = !searchTerm || 
      task.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    const matchesProject = projectFilter === 'all' || task.project_name === projectFilter;
    
    return matchesSearch && matchesStatus && matchesProject;
  });

  // Calculate stats
  const stats = {
    total: tasks.length,
    completed: tasks.filter(t => t.status === 'completed').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    notStarted: tasks.filter(t => t.status === 'not_started').length,
    totalValue: tasks.reduce((sum, t) => sum + (t.total || 0), 0)
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'completed': return <CheckCircle2 size={16} className="text-green-500" />;
      case 'in_progress': return <Clock size={16} className="text-blue-500" />;
      case 'not_started': return <Circle size={16} className="text-stone-400" />;
      default: return <AlertCircle size={16} className="text-amber-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'completed': 'bg-green-100 text-green-700',
      'in_progress': 'bg-blue-100 text-blue-700',
      'not_started': 'bg-stone-100 text-stone-600',
      'delayed': 'bg-red-100 text-red-700',
      'blocked': 'bg-red-100 text-red-700'
    };
    return variants[status] || 'bg-stone-100 text-stone-600';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="vendor-tasks-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-stone-800">My Tasks</h1>
            <p className="text-stone-500 mt-1">View and update your assigned tasks</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="apple-card p-4">
            <p className="text-sm text-stone-500">Total Tasks</p>
            <p className="text-2xl font-bold text-stone-800">{stats.total}</p>
          </div>
          <div className="apple-card p-4">
            <p className="text-sm text-stone-500">Completed</p>
            <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
          </div>
          <div className="apple-card p-4">
            <p className="text-sm text-stone-500">In Progress</p>
            <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
          </div>
          <div className="apple-card p-4">
            <p className="text-sm text-stone-500">Not Started</p>
            <p className="text-2xl font-bold text-stone-600">{stats.notStarted}</p>
          </div>
          <div className="apple-card p-4">
            <p className="text-sm text-stone-500">Total Value</p>
            <p className="text-2xl font-bold text-purple-600">₹{stats.totalValue.toLocaleString()}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-tasks"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="not_started">Not Started</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="w-[200px]" data-testid="project-filter">
              <SelectValue placeholder="Project" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {projects.map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tasks List */}
        {filteredTasks.length === 0 ? (
          <div className="apple-card p-12 text-center">
            <ListTodo size={48} className="mx-auto text-stone-300 mb-4" />
            <h3 className="text-lg font-medium text-stone-600 mb-2">No Tasks Assigned</h3>
            <p className="text-stone-400 text-sm">
              {tasks.length === 0 
                ? "You haven't been assigned any tasks yet."
                : "Try adjusting your search or filters."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTasks.map((task) => (
              <div 
                key={task.id} 
                className="apple-card p-4 hover:shadow-md transition-shadow"
                data-testid={`vendor-task-${task.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusIcon(task.status)}
                      <h3 className="font-semibold text-stone-800">{task.name}</h3>
                      <Badge className={getStatusBadge(task.status)}>
                        {task.status?.replace('_', ' ')}
                      </Badge>
                    </div>
                    
                    {task.description && (
                      <p className="text-sm text-stone-500 mb-3 line-clamp-2">{task.description}</p>
                    )}
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm text-stone-500">
                      <span className="flex items-center gap-1">
                        <Package size={14} />
                        {task.project_name}
                      </span>
                      {task.category_name && (
                        <span className="text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full text-xs">
                          {task.category_name}
                        </span>
                      )}
                      {task.start_date && (
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          {formatDate(task.start_date)} 
                          {task.end_date && ` - ${formatDate(task.end_date)}`}
                        </span>
                      )}
                    </div>
                    
                    {/* Progress Bar - iter-386u-j: bracket-based colours */}
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-stone-500">Progress</span>
                        <span className="font-medium text-stone-700">{task.progress_percentage || 0}%</span>
                      </div>
                      <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${getProgressBgClass(task.progress_percentage || 0)} rounded-full transition-all`}
                          style={{ width: `${task.progress_percentage || 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-end gap-3 shrink-0">
                    <div className="text-right">
                      <div className="text-sm text-stone-400 mb-1">
                        {task.quantity} {task.unit} × ₹{task.rate?.toLocaleString()}
                      </div>
                      <div className="text-lg font-bold text-purple-600">
                        ₹{task.total?.toLocaleString()}
                      </div>
                    </div>
                    
                    {/* Edit Button */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditTask(task)}
                      className="text-purple-600 border-purple-300 hover:bg-purple-50"
                      data-testid={`edit-task-${task.id}`}
                    >
                      <Edit2 size={14} className="mr-1" />
                      Update
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit Task Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-md" data-testid="edit-task-dialog">
            <DialogHeader>
              <DialogTitle className="text-stone-900 font-manrope text-xl">
                Update Task
              </DialogTitle>
            </DialogHeader>
            
            {selectedTask && (
              <div className="space-y-4 py-4">
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="font-semibold text-stone-800">{selectedTask.name}</p>
                  <p className="text-sm text-stone-500">{selectedTask.project_name}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-stone-700">Start Date</Label>
                    <Input
                      type="date"
                      value={editData.start_date}
                      onChange={(e) => setEditData({...editData, start_date: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-stone-700">End Date</Label>
                    <Input
                      type="date"
                      value={editData.end_date}
                      onChange={(e) => setEditData({...editData, end_date: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-stone-700">Status</Label>
                  <Select value={editData.status} onValueChange={(v) => setEditData({...editData, status: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_started">Not Started</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-stone-700">Progress: {editData.progress_percentage}%</Label>
                  <div className="mt-2">
                    <ProgressSlider
                      progress={editData.progress_percentage}
                      onChange={(value) => setEditData({...editData, progress_percentage: value})}
                    />
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="text-stone-700">
                Cancel
              </Button>
              <Button 
                onClick={handleSaveTask}
                disabled={saving}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
